// =============================================================================
// Origin UI — src/pages/evaluations/MyEvaluationPage.tsx
// Employee's own evaluation form. Dynamically renders sections and questions
// from the cycle template. Supports auto-save, submit, and acknowledge.
// Phase 7 — Year-End Evaluation & Probation Review module.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { evaluationApi, EvaluationDetail, SaveResponseItem } from '@/services/evaluationApi';
import { generateEvaluationPdf } from '@/services/evaluationPdfGenerator';
import styles from './evaluations.module.css';

export function MyEvaluationPage() {
  
  const [evaluation, setEvaluation] = useState<EvaluationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [noActiveCycle, setNoActiveCycle] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<Map<string, SaveResponseItem>>(new Map());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await evaluationApi.getMyEvaluation();
      if ('message' in result) {
        setNoActiveCycle(true);
        setEvaluation(null);
      } else {
        setEvaluation(result);
        setNoActiveCycle(false);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load evaluation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-save: debounce 2 seconds after last change
  const scheduleAutoSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!evaluation || pendingChangesRef.current.size === 0) return;
      try {
        setSaving(true);
        const responses = Array.from(pendingChangesRef.current.values());
        await evaluationApi.saveResponses(evaluation.evaluationId, responses);
        pendingChangesRef.current.clear();
        setLastSaved(new Date());
      } catch (e: any) {
        console.error('Auto-save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 2000);
  }, [evaluation]);

  const handleResponseChange = useCallback((questionId: string, optionId: string | null, comment: string | null) => {
    pendingChangesRef.current.set(questionId, {
      templateQuestionId: questionId,
      ratingScaleOptionId: optionId,
      comment,
    });
    // Also update local state for immediate UI feedback
    setEvaluation(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map(s => ({
          ...s,
          questions: s.questions.map(q => {
            if (q.templateQuestionId !== questionId) return q;
            const selectedOption = q.ratingOptions?.find(o => o.ratingScaleOptionId === optionId);
            return {
              ...q,
              ratingScaleOptionId: optionId,
              selectedRatingLabel: selectedOption?.label ?? null,
              selectedRatingValue: selectedOption?.numericValue ?? null,
              comment: comment ?? q.comment,
            };
          }),
        })),
      };
    });
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  function getIncompleteQuestions() {
    if (!evaluation) return [];
    return evaluation.sections
      .filter(s => s.respondent === 'Employee')
      .flatMap(s => s.questions)
      .filter(q => q.isRequired)
      .filter(q => {
        if (q.questionType === 'Rating') return !q.ratingScaleOptionId || !q.comment?.trim();
        if (q.questionType === 'FreeText') return !q.comment?.trim();
        return false;
      })
      .map(q => q.title);
  }

  const handleSubmit = async () => {
    if (!evaluation) return;
    if (!confirm('Once submitted, you won\u2019t be able to edit your self-assessment. Continue?')) return;
    try {
      // Save any pending changes first
      if (pendingChangesRef.current.size > 0) {
        await evaluationApi.saveResponses(evaluation.evaluationId, Array.from(pendingChangesRef.current.values()));
        pendingChangesRef.current.clear();
      }
      await evaluationApi.submitEvaluation(evaluation.evaluationId);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to submit');
    }
  };

  const handleAcknowledge = async () => {
    if (!evaluation) return;
    if (!confirm('This confirms you have viewed and acknowledge the manager\u2019s review. Continue?')) return;
    try {
      await evaluationApi.acknowledgeEvaluation(evaluation.evaluationId);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to acknowledge');
    }
  };

  if (loading) return <div className={styles.page}><div className={styles.loading}>Loading evaluation...</div></div>;
  if (noActiveCycle) return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>My Evaluation</h1>
      <div className={styles.emptyState}>No evaluation cycle is currently open.</div>
    </div>
  );
  if (error) return <div className={styles.page}><div className={styles.errorBanner}>{error}</div></div>;
  if (!evaluation) return null;

  const isMidYear = evaluation.cycleType === 'MidYearReview';
  const isEmployeeEditable = isMidYear
    ? evaluation.status === 'ManagerCompleted'  // Mid-year: employee can only add comment after manager completes
    : (evaluation.status === 'NotStarted' || evaluation.status === 'EmployeeDraft');
  const showManagerSections = evaluation.status === 'ManagerCompleted' || evaluation.status === 'Acknowledged';
  const showAcknowledge = evaluation.status === 'ManagerCompleted';
  const canSubmit = !isMidYear && isEmployeeEditable;  // No submit step for mid-year
  const isMidYearWaiting = isMidYear && (evaluation.status === 'NotStarted' || evaluation.status === 'ManagerDraft');



  const deadlineDate = new Date(evaluation.employeeDeadline);
  const now = new Date();
  const daysUntilDeadline = Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysUntilDeadline < 0;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h1 className={styles.pageTitle}>My Evaluation</h1>
        <button className={styles.backBtn} onClick={() => generateEvaluationPdf(evaluation, 'Employee')}>Print PDF</button>
      </div>

      {/* Status banner */}
      <div className={`${styles.statusBanner} ${styles[`status_${evaluation.status}`] || ''}`}>
        <div className={styles.statusText}>
          <strong>Status:</strong> {formatStatus(evaluation.status)}
          {isEmployeeEditable && (
            <span className={isOverdue ? styles.overdue : styles.deadline}>
              {isOverdue
                ? ` — ${Math.abs(daysUntilDeadline)} days overdue`
                : ` — Due ${deadlineDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
              }
              {!isOverdue && daysUntilDeadline <= 7 && ` (${daysUntilDeadline} days left)`}
            </span>
          )}
        </div>
        <div className={styles.saveIndicator}>
          {saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : ''}
        </div>
      </div>

      <div className={styles.card} style={{ textAlign: 'center', padding: '16px 20px' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0A2240' }}>{evaluation.cycleName}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7885' }}>{evaluation.cycleType === 'MidYearReview' ? 'Mid-Year Review' : evaluation.cycleType === 'ProbationReview' ? 'Probation Review' : 'Year-End Evaluation'} — {evaluation.reviewYear}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6B7885' }}>
                <span style={{ width: 12, height: 12, background: '#FF8300', borderRadius: 2, display: 'inline-block' }}></span> Manager Section
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6B7885' }}>
                <span style={{ width: 12, height: 12, background: '#00B0B9', borderRadius: 2, display: 'inline-block' }}></span> Employee Section
              </span>
            </div>
      </div>

      {/* Section 1: Employee Information (always shown) */}
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Employee Information</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Employee Name</span>
            <span className={styles.infoValue}>{evaluation.employeeName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Job Title</span>
            <span className={styles.infoValue}>{evaluation.employeeJobTitle}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Line Manager</span>
            <span className={styles.infoValue}>{evaluation.managerName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Year in Review</span>
            <span className={styles.infoValue}>{evaluation.reviewYear}</span>
          </div>
        </div>
      </div>

      {/* Rating Definitions — show the primary scale's definitions at the top */}
      {(() => {
        const allOptions = evaluation.sections
          .flatMap(s => s.questions)
          .filter(q => q.ratingOptions && q.ratingOptions.length > 0)
          .map(q => q.ratingOptions!);
        const primaryScale = allOptions.length > 0
          ? allOptions.reduce((a, b) => a.length >= b.length ? a : b)
          : [];
        const defs = primaryScale.filter(o => o.description);
        if (defs.length === 0) return null;
        return (
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Rating Definitions</h2>
            <div style={{ display: 'flex', border: '1px solid #E2E7EC', borderRadius: 8, overflow: 'hidden', margin: '16px 20px 4px' }}>
              {defs.map(opt => {
                const colorMap: Record<string, [string, string]> = {
                  'Outstanding': ['#00AA6E', '#E6F7F0'],
                  'Exceeds Expectations': ['#00B0B9', '#E0F7F8'],
                  'Meets Expectations': ['#FF8300', '#FFF3E0'],
                  'Needs Improvement': ['#F04B41', '#FDE8E7'],
                  'Unacceptable': ['#C33A30', '#FDE8E7'],
                };
                const [bg, lightBg] = colorMap[opt.label] || ['#6B7885', '#F1F4F6'];
                return (
                  <div key={opt.ratingScaleOptionId} style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #E2E7EC' }}>
                    <div style={{ background: bg, color: '#fff', padding: '10px 8px', textAlign: 'center', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {opt.label}
                    </div>
                    <div style={{ padding: '10px 8px', background: lightBg, fontSize: 11, lineHeight: 1.5, color: '#4A5568', flex: 1 }}>
                      {opt.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Mid-year: waiting for manager to complete */}
      {isMidYearWaiting && (
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6B7885' }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#0A2240' }}>Pending Manager Review</div>
            <p style={{ fontSize: 14, lineHeight: 1.6 }}>Your manager is currently reviewing your performance. You will be able to view their feedback and add your comments once the review is complete.</p>
          </div>
        </div>
      )}

      {/* Dynamic sections from template */}
      {!isMidYearWaiting && evaluation.sections
        .filter(s => !s.isSystem)
        .map(section => {
          const isEmployeeSection = section.respondent === 'Employee';
          const isManagerSection = section.respondent === 'Manager';
          const isEditable = isEmployeeSection && isEmployeeEditable;
          const isVisible = isMidYear
            ? (showManagerSections || evaluation.status === 'Acknowledged')  // Mid-year: show all after manager completes
            : (isEmployeeSection || (isManagerSection && showManagerSections));
          const isPending = !isMidYear && isManagerSection && !showManagerSections;

          if (!isVisible && !isPending) return null;

          return (
            <div key={section.templateSectionId} className={`${styles.card} ${isPending ? styles.cardPending : ''}`} style={{ borderLeft: isManagerSection ? '4px solid #FF8300' : isEmployeeSection ? '4px solid #00B0B9' : undefined }}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {section.description && <p className={styles.sectionDescription}>{section.description}</p>}

              {isPending ? (
                <div className={styles.pendingMessage}>Pending manager review</div>
              ) : (
                section.questions.map(q => (
                  <div key={q.templateQuestionId} className={styles.questionBlock}>
                    <div className={styles.questionHeader}>
                      <h3 className={styles.questionTitle}>
                        {q.title}
                        {q.isRequired && <span className={styles.required}>*</span>}
                      </h3>
                      {q.description && <p className={styles.questionDescription}>{q.description}</p>}
                    </div>

                    {q.questionType === 'Rating' && q.ratingOptions && (
                      <div className={styles.ratingGroup}>
                        {q.ratingOptions.map(opt => (
                          <label
                            key={opt.ratingScaleOptionId}
                            className={`${styles.ratingOption} ${q.ratingScaleOptionId === opt.ratingScaleOptionId ? styles.ratingSelected : ''} ${!isEditable ? styles.ratingDisabled : ''}`}
                          >
                            <input
                              type="radio"
                              name={`rating_${q.templateQuestionId}`}
                              value={opt.ratingScaleOptionId}
                              checked={q.ratingScaleOptionId === opt.ratingScaleOptionId}
                              disabled={!isEditable}
                              onChange={() => handleResponseChange(q.templateQuestionId, opt.ratingScaleOptionId, q.comment)}
                              className={styles.ratingRadio}
                            />
                            <span className={styles.ratingLabel}>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {q.questionType === 'FreeText' && (
                      <textarea
                        className={styles.freeText}
                        value={q.comment || ''}
                        disabled={!isEditable}
                        placeholder={isEditable ? 'Enter your response...' : ''}
                        rows={6}
                        onChange={(e) => handleResponseChange(q.templateQuestionId, null, e.target.value)}
                      />
                    )}

                    {/* T3: Mandatory comment on every rating question */}
                    {q.questionType === 'Rating' && (
                      <textarea
                        className={styles.commentField}
                        value={q.comment || ''}
                        disabled={!isEditable}
                        placeholder={isEditable ? 'Why did you give this rating? *' : ''}
                        rows={3}
                        onChange={(e) => handleResponseChange(q.templateQuestionId, q.ratingScaleOptionId, e.target.value)}
                      />
                    )}

                    {/* Show overall rating prominently if this is the overall rating question */}
                    {q.isOverallRating && q.selectedRatingLabel && !isEditable && (
                      <div className={styles.overallRatingBadge}>
                        Overall Rating: <strong>{q.selectedRatingLabel}</strong>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          );
        })}

      {/* Action buttons */}
      <div className={styles.actionBar}>
        {canSubmit && (
          <>
            <button
              className={styles.submitBtn}
              onClick={() => {
                const incomplete = getIncompleteQuestions();
                if (incomplete.length > 0) { setValidationErrors(incomplete); return; }
                setValidationErrors([]);
                handleSubmit();
              }}
            >
              Submit Self-Assessment
            </button>
            {validationErrors.length > 0 && (
              <div style={{ marginTop: 12, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#991B1B' }}>
                <strong>Please complete the following questions:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {validationErrors.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
        {showAcknowledge && (
          <>
            <button
              className={styles.acknowledgeBtn}
              onClick={() => {
                const incomplete = getIncompleteQuestions();
                if (incomplete.length > 0) { setValidationErrors(incomplete); return; }
                setValidationErrors([]);
                handleAcknowledge();
              }}
            >
              Acknowledge Review
            </button>
            {validationErrors.length > 0 && (
              <div style={{ marginTop: 12, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#991B1B' }}>
                <strong>Please complete the following questions before acknowledging:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {validationErrors.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
        {evaluation.status === 'EmployeeSubmitted' && !isMidYear && (
          <div className={styles.waitingMessage}>
            Your self-assessment has been submitted. Waiting for manager review.
          </div>
        )}
        {isMidYearWaiting && (
          <div className={styles.waitingMessage}>
            Your manager is reviewing your performance. You will be notified once complete.
          </div>
        )}
        {evaluation.status === 'Acknowledged' && (
          <div className={styles.completedMessage}>
            This evaluation is complete. You acknowledged on {new Date(evaluation.employeeAcknowledgedAt!).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
          </div>
        )}
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case 'NotStarted': return 'Not Started';
    case 'EmployeeDraft': return 'In Progress (Draft)';
    case 'EmployeeSubmitted': return 'Submitted — Awaiting Manager Review';
    case 'ManagerDraft': return 'Manager Reviewing';
    case 'ManagerCompleted': return 'Manager Review Complete — Please Acknowledge';
    case 'Acknowledged': return 'Complete';
    default: return status;
  }
}
